import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { render } from "react-dom"
import { SelectLocation } from "web/components/SelectLocation/SelectLocation"
import "mapbox-gl/dist/mapbox-gl.css"

const LATITUDE_BOUNDARY = 90
const LONGITUDE_BOUNDARY = 180

export default class MapController extends Controller {
  static values = {
    zoom: String,
    style: String,
    heightClass: { type: String, default: "h-[375px]" },
  }

  static outlets = ["session-token"]

  static targets = [
    "mapElement",
    "hiddenPlace",
    "hiddenLatitude",
    "hiddenLongitude",
    "identityLatitude",
    "identityLongitude",
  ]

  connect() {
    this.renderMap()
  }

  renderMap(location) {
    const mapClasses = `relative ${this.heightClassValue} overflow-hidden`
    render(
      <div className={mapClasses}>
        <SelectLocation
          setLocation={this.handleLocationClick}
          location={location}
          simpleSelection={true}
        />
      </div>,
      this.mapElementTarget
    )
  }

  handleLocationClick = (location) => {
    this.dispatch("map-coordinates-manually-set")

    this.setHiddenLocationValues({
      latitude: location.latitude,
      longitude: location.longitude,
      city: location.city,
      state: location.state,
      country: location.country,
    })

    this.renderMap(location)
  }

  locationPlaceChange(e) {
    const placeId = e.target.value
    this.getPlace(placeId)
  }

  locationCoordsChange() {
    if (this.hiddenLatitudeTarget.value && this.hiddenLongitudeTarget.value) {
      const location = {
        latitude: this.hiddenLatitudeTarget.value,
        longitude: this.hiddenLongitudeTarget.value,
      }

      this.setHiddenLocationValues(location)
      this.renderMap(location)
    }
  }

  async getPlace(placeId) {
    const placeDetailsRequest = await get(
      `/api/google_maps/place_details/${placeId}?session_token=${this.sessionTokenOutlet.token}`,
      { responseKind: "json" }
    )

    if (placeDetailsRequest.ok) {
      const placeDetails = await placeDetailsRequest.json

      this.setHiddenLocationValues({ placeId: placeId })
      this.renderMap(placeDetails)

      this.sessionTokenOutlet.rotate()
    }
  }

  latLongBlurred() {
    this.#ensureValidIdentityCoordValues()

    const location = {
      latitude: this.identityLatitudeTarget.value,
      longitude: this.identityLongitudeTarget.value,
    }

    this.setHiddenLocationValues(location)

    if (
      this.identityLatitudeTarget.value &&
      this.identityLongitudeTarget.value
    ) {
      this.dispatch("map-coordinates-manually-set")
      this.renderMap(location)
    }
  }

  #ensureValidIdentityCoordValues() {
    if (parseFloat(this.identityLatitudeTarget.value) > LATITUDE_BOUNDARY) {
      this.identityLatitudeTarget.value = LATITUDE_BOUNDARY
    } else if (
      parseFloat(this.identityLatitudeTarget.value) < -LATITUDE_BOUNDARY
    ) {
      this.identityLatitudeTarget.value = -LATITUDE_BOUNDARY
    }

    if (parseFloat(this.identityLongitudeTarget.value) > LONGITUDE_BOUNDARY) {
      this.identityLongitudeTarget.value = LONGITUDE_BOUNDARY
    } else if (
      parseFloat(this.identityLongitudeTarget.value) < -LONGITUDE_BOUNDARY
    ) {
      this.identityLongitudeTarget.value = -LONGITUDE_BOUNDARY
    }
  }

  setHiddenLocationValues(locationObject) {
    this.identityLatitudeTarget.value = locationObject.latitude
      ? parseFloat(locationObject.latitude).toFixed(4)
      : null
    this.identityLongitudeTarget.value = locationObject.longitude
      ? parseFloat(locationObject.longitude).toFixed(4)
      : null

    this.hiddenLatitudeTarget.value = locationObject.latitude || null
    this.hiddenLongitudeTarget.value = locationObject.longitude || null
    this.hiddenPlaceTarget.value = locationObject.placeId || null
  }
}
